import { DcmJsDatasetWrapper } from './dcmJsWrapper';
import { naturalizeDataset } from './engine/compat';
import { DicomDataset } from './engine/dataset';
import { elementFromNaturalValue } from './engine/denaturalize';
import { tagForKeyword } from './engine/dictionary';
import { Tag } from './engine/tag';
import * as tags from './engine/tags';

/**
 * Reads one attribute of one ROI without projecting the whole structure set.
 *
 * The keyword-addressed projection is built for a whole dataset, and for a
 * structure set most of that dataset is contour points: projecting it parses
 * every coordinate out of its decimal string, which is where a structure set's
 * parse cost lives. Reading an ROI's name or colour needs none of that.
 *
 * So the projection is applied to the single sequence item instead — a handful
 * of attributes, no contour data. Reusing the same function the whole-dataset
 * projection uses is deliberate: it makes the value's shape identical by
 * construction rather than by a second implementation that has to agree.
 */

/** The sequences an ROI's attributes are spread across, and how each keys them. */
export const ROI_SEQUENCES = {
    /** StructureSetROISequence, keyed by ROINumber. */
    roi: {
        name: 'StructureSetROISequence',
        sequence: tags.STRUCTURE_SET_ROI_SEQUENCE,
        key: tags.ROI_NUMBER,
    },
    /** ROIContourSequence, keyed by ReferencedROINumber. */
    contour: {
        name: 'ROIContourSequence',
        sequence: tags.ROI_CONTOUR_SEQUENCE,
        key: tags.REFERENCED_ROI_NUMBER,
    },
    /** RTROIObservationsSequence, keyed by ReferencedROINumber. */
    observations: {
        name: 'RTROIObservationsSequence',
        sequence: tags.RT_ROI_OBSERVATIONS_SEQUENCE,
        key: tags.REFERENCED_ROI_NUMBER,
    },
} as const;

export type RoiSequence = keyof typeof ROI_SEQUENCES;

/** The item of `sequence` whose key attribute is `roiNumber`. */
function findItem(
    dataset: DicomDataset,
    sequenceTag: Tag,
    keyTag: Tag,
    roiNumber: number
): DicomDataset | undefined {
    return dataset
        .sequence(sequenceTag)
        .find((item) => item.number(keyTag) === roiNumber);
}

/**
 * The outcome of reading an attribute through the typed model: either the
 * value, whatever it turned out to be, or that the typed model could not
 * answer and the caller has to project.
 *
 * A discriminated result rather than a sentinel value, because every value a
 * sentinel could take is also a value an attribute can have. `null` was that
 * sentinel, and `null` is exactly what the projection uses for an attribute
 * that is present and carries nothing — which structure sets are full of, ROI
 * descriptions and interpreted types among them. Each of those read as
 * "cannot answer" and sent the caller to build the whole projection, so the
 * commonest attributes were the ones that defeated the fast path.
 */
export type TypedPropertyRead<T> =
    | { readonly answered: true; readonly value: T | undefined }
    | { readonly answered: false };

const UNANSWERED: TypedPropertyRead<never> = { answered: false };

/**
 * Reads an ROI attribute through the typed model, or reports that it could
 * not — because the dataset was assembled in memory rather than parsed, and so
 * has no typed form for this to read.
 */
export function readRoiProperty<T>(
    wrapper: DcmJsDatasetWrapper,
    which: RoiSequence,
    roiNumber: number,
    propertyName: string
): TypedPropertyRead<T> {
    const dataset = wrapper.typed;
    if (!dataset) {
        return UNANSWERED;
    }
    if (tagForKeyword(propertyName) === undefined) {
        // A private or unrecognised attribute, which the typed model keys by
        // tag rather than by this name. Left to the projection.
        return UNANSWERED;
    }

    const { sequence, key } = ROI_SEQUENCES[which];
    const item = findItem(dataset, sequence, key, roiNumber);
    if (!item) {
        // The ROI has no item in this sequence, which is an answer: the
        // attribute is absent, and projecting would not find it either.
        return { answered: true, value: undefined };
    }
    return {
        answered: true,
        value: naturalizeDataset(item)[propertyName] as T | undefined,
    };
}

/**
 * Writes an ROI attribute through the typed model, reporting whether it could.
 *
 * False means the dataset has no typed form to write into — it was assembled
 * in memory — or the attribute is one the typed model does not key by this
 * name, and the caller has to write through the projection instead. Taking the
 * projection to write one attribute re-encodes every other attribute on the
 * next save, contour points included, so an edit to a structure name becomes a
 * diff against the whole object.
 */
export function writeRoiProperty(
    wrapper: DcmJsDatasetWrapper,
    which: RoiSequence,
    roiNumber: number,
    propertyName: string,
    value: unknown,
    allowNewRoi = false
): boolean {
    const dataset = wrapper.typed;
    if (!dataset) {
        return false;
    }
    const tag = tagForKeyword(propertyName);
    if (tag === undefined) {
        return false;
    }

    const item = itemForWriting(dataset, which, roiNumber, allowNewRoi);
    if (!item) {
        throw new RoiItemNotFoundError(which, roiNumber);
    }

    const element = elementFromNaturalValue(tag, value, item);
    if (element === undefined) {
        return false;
    }
    item.put(element);
    return true;
}

/**
 * Removes an ROI attribute through the typed model, reporting whether it
 * could. An ROI with no item in the sequence is nothing to remove, which is
 * an outcome and not a failure.
 */
export function deleteRoiProperty(
    wrapper: DcmJsDatasetWrapper,
    which: RoiSequence,
    roiNumber: number,
    propertyName: string
): boolean {
    const dataset = wrapper.typed;
    if (!dataset) {
        return false;
    }
    const tag = tagForKeyword(propertyName);
    if (tag === undefined) {
        return false;
    }

    const { sequence, key } = ROI_SEQUENCES[which];
    findItem(dataset, sequence, key, roiNumber)?.delete(tag);
    return true;
}

/** An ROI that has no item in the sequence an attribute was to be written to. */
export class RoiItemNotFoundError extends Error {
    constructor(which: RoiSequence, roiNumber: number) {
        super(
            `ROI with ROINumber ${roiNumber} not found from ` +
                `${ROI_SEQUENCES[which].name}`
        );
        this.name = 'RoiItemNotFoundError';
    }
}

/**
 * The item to write into, appended to the sequence when `create` is set and
 * the ROI has none.
 *
 * A new item carries the number that keys it from the moment it exists, so
 * the item and the key that finds it cannot come apart — which is the failure
 * an ROI spread across three parallel sequences invites.
 */
function itemForWriting(
    dataset: DicomDataset,
    which: RoiSequence,
    roiNumber: number,
    create: boolean
): DicomDataset | undefined {
    const { sequence, key } = ROI_SEQUENCES[which];
    const existing = findItem(dataset, sequence, key, roiNumber);
    if (existing || !create) {
        return existing;
    }

    const item = new DicomDataset();
    // PS3.3 C.12.1.1.2: a nested dataset is read in the enclosing dataset's
    // character set, so it has to be written in that set too.
    item.setCharset(dataset.specificCharset);
    item.setNumber(key, roiNumber, 'IS');
    dataset.setSequence(sequence, [...dataset.sequence(sequence), item]);
    return item;
}

/**
 * Removes an ROI's entries from all three sequences, reporting whether the
 * typed model could do it.
 *
 * One operation, because an ROI is one thing: it exists as three parallel
 * items keyed by its number, and a removal that reaches two of them leaves a
 * structure set that is internally inconsistent rather than one that fails.
 */
export function removeRoi(
    wrapper: DcmJsDatasetWrapper,
    roiNumber: number
): boolean {
    const dataset = wrapper.typed;
    if (!dataset) {
        return false;
    }

    for (const { sequence, key } of Object.values(ROI_SEQUENCES)) {
        const remaining = dataset
            .sequence(sequence)
            .filter((item) => item.number(key) !== roiNumber);
        if (remaining.length !== dataset.sequence(sequence).length) {
            dataset.setSequence(sequence, remaining);
        }
    }
    return true;
}

/**
 * The frame of reference a structure set's ROIs belong to, or undefined when
 * the typed model cannot answer.
 *
 * Distinguishes "no typed form" from "no frame of reference": the second is a
 * structure set the application refuses to add a structure to, and reporting
 * it as the first would send the caller to the projection to find the same
 * nothing.
 */
export function readStructureSetFrameOfReference(
    wrapper: DcmJsDatasetWrapper
): TypedPropertyRead<string> {
    const dataset = wrapper.typed;
    if (!dataset) {
        return UNANSWERED;
    }
    const first = dataset.sequence(
        tags.REFERENCED_FRAME_OF_REFERENCE_SEQUENCE
    )[0];
    return {
        answered: true,
        value: first?.string(tags.FRAME_OF_REFERENCE_UID),
    };
}

/**
 * Reads a top-level attribute through the typed model, or reports that it
 * could not.
 *
 * The value takes the same shape the projection would give it — a person name
 * as `{ Alphabetic }`, a decimal string as a number — because it is produced
 * by the same routine, applied to the one element rather than to the dataset.
 * Callers were written against those shapes, and a second rendering of them is
 * how an attribute read one way comes to differ from the same attribute read
 * the other.
 */
export function readDatasetProperty<T>(
    wrapper: DcmJsDatasetWrapper,
    propertyName: string
): TypedPropertyRead<T> {
    const dataset = wrapper.typed;
    if (!dataset) {
        return UNANSWERED;
    }
    const tag = tagForKeyword(propertyName);
    if (tag === undefined) {
        return UNANSWERED;
    }
    return {
        answered: true,
        value: naturalizeAttribute(dataset, tag)[propertyName] as T | undefined,
    };
}

/**
 * Writes a top-level attribute through the typed model, reporting whether it
 * could.
 */
export function writeDatasetProperty(
    wrapper: DcmJsDatasetWrapper,
    propertyName: string,
    value: unknown
): boolean {
    const dataset = wrapper.typed;
    if (!dataset) {
        return false;
    }
    const tag = tagForKeyword(propertyName);
    if (tag === undefined) {
        return false;
    }
    const element = elementFromNaturalValue(tag, value, dataset);
    if (element === undefined) {
        return false;
    }
    dataset.put(element);
    return true;
}

/**
 * One attribute of `dataset` in the naturalized shape.
 *
 * Naturalizing the whole dataset to read one attribute would descend into
 * every sequence it holds — for a structure set, every contour point of every
 * ROI — so the element is lifted into a dataset of its own, carrying the
 * character set its text was read under.
 */
function naturalizeAttribute(
    dataset: DicomDataset,
    tag: Tag
): Record<string, unknown> {
    const element = dataset.element(tag);
    if (element === undefined) {
        return {};
    }
    const single = new DicomDataset();
    single.setCharset(dataset.specificCharset);
    single.put(element);
    return naturalizeDataset(single) as unknown as Record<string, unknown>;
}
